/**
 * Text Fitting Engine
 *
 * Uses Canvas measureText API with Inter font to determine how text fits
 * in various constrained regions (clue cells, recap rects, etc.).
 *
 * Font sizes (normal / smaller):
 *   Clue cells:     28 / 22 px, weight 700
 *   Recap pairs:    21 / 17 px, weight 600
 *   Recap answers:  37 / 30 px (answer), 21 / 17 px (context), weight 800/600
 *   Recap category: 21 / 17 px, weight 600, italic
 */

import type { TextFitResult, DisplayHints } from '../types';

// ─── Measurer ────────────────────────────────────────────

let sharedCanvas: HTMLCanvasElement | null = null;
let sharedCtx: CanvasRenderingContext2D | null = null;
const measureCache = new Map<string, number>();

function getCtx(): CanvasRenderingContext2D {
  if (!sharedCtx) {
    sharedCanvas = document.createElement('canvas');
    sharedCtx = sharedCanvas.getContext('2d')!;
  }
  return sharedCtx;
}

function measure(text: string, font: string): number {
  const key = `${font}|${text}`;
  const cached = measureCache.get(key);
  if (cached !== undefined) return cached;
  const ctx = getCtx();
  ctx.font = font;
  const width = ctx.measureText(text).width;
  measureCache.set(key, width);
  return width;
}

/** Wait for Inter font to load before first measurement */
let fontReady: Promise<void> | null = null;
export function ensureFontReady(): Promise<void> {
  if (fontReady) return fontReady;
  if (typeof document === 'undefined') return Promise.resolve();
  fontReady = document.fonts.ready.then(() => {
    measureCache.clear(); // re-measure with loaded font
  });
  return fontReady;
}

// ─── Display Hints ───────────────────────────────────────

/**
 * Apply display hints (hyphens and line breaks) to text.
 * Returns array of lines with hyphens inserted.
 */
export function applyDisplayHints(
  text: string,
  hints?: DisplayHints
): string[] {
  if (!hints) return [text];

  const breaks = new Set(hints.lineBreaks ?? []);
  const hyphens = new Set(hints.hyphens ?? []);

  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += text[i];
    if (hyphens.has(i)) result += '-';
    if (breaks.has(i)) result += '\n';
  }

  return result.split('\n');
}

// ─── Clue Cell Fitting ───────────────────────────────────

const CLUE_NORMAL = 28;
const CLUE_SMALLER = 22;
const CLUE_MIN = 14;
const CLUE_WEIGHT = 700;
const CLUE_CELL_PADDING = 24; // 12px each side
const CLUE_CELL_WIDTH = 196;
// Default available = CLUE_CELL_WIDTH - CLUE_CELL_PADDING = 172px

function clueFont(size: number): string {
  return `${CLUE_WEIGHT} ${size}px Inter, sans-serif`;
}

/**
 * Fit text into a 196x66 clue cell.
 * Tries 3 layouts: single-line 28px, single-line 22px, two-line 22px.
 * Two-line text always uses the smaller font so it never exceeds a one-line clue.
 */
export function fitClueText(
  text: string,
  width?: number,
  hints?: DisplayHints
): TextFitResult {
  const available = (width ?? CLUE_CELL_WIDTH) - CLUE_CELL_PADDING;
  const upper = text.toUpperCase();
  const hintLines = applyDisplayHints(upper, hints);

  // If display hints force specific line breaks, use them directly.
  // Supports 1, 2, or 3 lines. Multi-line shrinks to the largest font that fits.
  if (hints?.lineBreaks?.length) {
    if (hintLines.length === 1) {
      const font28 = clueFont(CLUE_NORMAL);
      if (measure(hintLines[0], font28) <= available) {
        return { lines: hintLines, fontSize: CLUE_NORMAL, isSmaller: false, needsManualFix: false };
      }
    }
    // Try CLUE_SMALLER first, then shrink down to CLUE_MIN
    for (let size = hintLines.length === 1 ? CLUE_NORMAL : CLUE_SMALLER; size >= CLUE_MIN; size--) {
      const font = clueFont(size);
      if (hintLines.every(l => measure(l, font) <= available)) {
        return { lines: hintLines, fontSize: size, isSmaller: size < CLUE_NORMAL, needsManualFix: false };
      }
    }
    return { lines: hintLines, fontSize: CLUE_MIN, isSmaller: true, needsManualFix: true };
  }

  // Extract optional hyphen positions for auto-fit break opportunities
  const hyphenPos = hints?.hyphens?.length ? hints.hyphens : undefined;

  // Auto-fit: try 3 layouts in order
  // 1. Single-line 28px
  const font28 = clueFont(CLUE_NORMAL);
  if (measure(upper, font28) <= available) {
    return { lines: [upper], fontSize: CLUE_NORMAL, isSmaller: false, needsManualFix: false };
  }

  // 2. Single-line 22px
  const font22 = clueFont(CLUE_SMALLER);
  if (measure(upper, font22) <= available) {
    return { lines: [upper], fontSize: CLUE_SMALLER, isSmaller: true, needsManualFix: false };
  }

  // 3. Two-line 22px (word boundaries + optional hyphens)
  // Two-line always uses smaller font so it never exceeds a one-line clue.
  const split22 = bestSplit(upper, font22, available, hyphenPos);
  if (split22) {
    return { lines: split22, fontSize: CLUE_SMALLER, isSmaller: true, needsManualFix: false };
  }

  // 4. Two-line shrunk below 22px — find largest font where the wider line fits
  for (let size = CLUE_SMALLER - 1; size >= CLUE_MIN; size--) {
    const font = clueFont(size);
    const split = bestSplit(upper, font, available, hyphenPos);
    if (split) {
      return { lines: split, fontSize: size, isSmaller: true, needsManualFix: false };
    }
  }

  // Nothing fits — flag for manual fix
  return { lines: [upper], fontSize: CLUE_MIN, isSmaller: true, needsManualFix: true };
}

// ─── Recap Pair Fitting ──────────────────────────────────

const RECAP_NORMAL = 21;
const RECAP_SMALLER = 17;
const RECAP_WEIGHT = 600;
const RECAP_RECT_WIDTH = 208;
const RECAP_RECT_PADDING = 24; // 12px each side
// Default available = RECAP_RECT_WIDTH - RECAP_RECT_PADDING = 184px

function recapFont(size: number, italic = false): string {
  return `${italic ? 'italic ' : ''}${RECAP_WEIGHT} ${size}px Inter, sans-serif`;
}

/**
 * Fit top clue and bottom clue into a 208x120 recap pair rect.
 *
 * Always produces exactly 2 lines: top clue on line 1, bottom clue on line 2.
 * Joiner words are floated to the end of line 1, start of line 2, or split
 * between them — whichever produces the most balanced appearance.
 *
 * If display hints force specific line breaks, those are used instead.
 */
export function fitRecapPairText(
  topClue: string,
  joiner: string | null,
  bottomClue: string,
  width?: number,
  hints?: DisplayHints
): TextFitResult {
  const available = (width ?? RECAP_RECT_WIDTH) - RECAP_RECT_PADDING;
  const top = topClue.toUpperCase();
  const bot = bottomClue.toUpperCase();

  // If display hints force specific line breaks, honour them
  if (hints?.lineBreaks?.length) {
    const joined = joiner ? `${top} ${joiner} ${bot}` : `${top} ${bot}`;
    const hintLines = applyDisplayHints(joined, hints);
    const font21 = recapFont(RECAP_NORMAL);
    const fits21 = hintLines.every(l => measure(l, font21) <= available);
    if (fits21 && hintLines.length <= 3) {
      return { lines: hintLines, fontSize: RECAP_NORMAL, isSmaller: false, needsManualFix: false };
    }
    const font17 = recapFont(RECAP_SMALLER);
    const fits17 = hintLines.every(l => measure(l, font17) <= available);
    return {
      lines: hintLines,
      fontSize: RECAP_SMALLER,
      isSmaller: true,
      needsManualFix: !fits17,
    };
  }

  // Build joiner placement candidates (top chunk + bottom chunk)
  const placements = getJoinerPlacements(top, joiner, bot);

  // Try each font size, with increasingly generous line counts
  for (const { size, font, isSmaller } of [
    { size: RECAP_NORMAL, font: recapFont(RECAP_NORMAL), isSmaller: false },
    { size: RECAP_SMALLER, font: recapFont(RECAP_SMALLER), isSmaller: true },
  ] as const) {
    // Phase 1: 1+1 lines (each half on one line — best case)
    {
      let best: TextFitResult | null = null;
      let bestDiff = Infinity;
      for (const p of placements) {
        const tw = measure(p.topChunk, font);
        const bw = measure(p.botChunk, font);
        if (tw <= available && bw <= available) {
          const diff = Math.abs(tw - bw);
          if (diff < bestDiff) {
            bestDiff = diff;
            best = { lines: [p.topChunk, p.botChunk], fontSize: size, isSmaller, needsManualFix: false, joinerOnLine: p.joinerOnLine, halfBreakAfterLine: 0 };
          }
        }
      }
      if (best) return best;
    }

    // Phase 2: 2+1 or 1+2 lines (one half wraps — 3 lines total)
    {
      let best: TextFitResult | null = null;
      let bestMaxW = Infinity;
      for (const p of placements) {
        const tw = measure(p.topChunk, font);
        const bw = measure(p.botChunk, font);

        // Top wraps to 2 lines, bottom stays 1
        if (tw > available && bw <= available) {
          const topSplit = bestSplit(p.topChunk, font, available);
          if (topSplit) {
            const maxW = Math.max(measure(topSplit[0], font), measure(topSplit[1], font), bw);
            if (maxW < bestMaxW) {
              bestMaxW = maxW;
              best = { lines: [...topSplit, p.botChunk], fontSize: size, isSmaller, needsManualFix: false, joinerOnLine: p.joinerOnLine, halfBreakAfterLine: 1 };
            }
          }
        }

        // Bottom wraps to 2 lines, top stays 1
        if (bw > available && tw <= available) {
          const botSplit = bestSplit(p.botChunk, font, available);
          if (botSplit) {
            const maxW = Math.max(tw, measure(botSplit[0], font), measure(botSplit[1], font));
            if (maxW < bestMaxW) {
              bestMaxW = maxW;
              best = { lines: [p.topChunk, ...botSplit], fontSize: size, isSmaller, needsManualFix: false, joinerOnLine: p.joinerOnLine, halfBreakAfterLine: 0 };
            }
          }
        }
      }
      if (best) return best;
    }

    // Phase 3: 2+2 lines (both halves wrap — 4 lines total)
    {
      let best: TextFitResult | null = null;
      let bestMaxW = Infinity;
      for (const p of placements) {
        const topSplit = bestSplit(p.topChunk, font, available);
        const botSplit = bestSplit(p.botChunk, font, available);
        if (topSplit && botSplit) {
          const maxW = Math.max(
            measure(topSplit[0], font), measure(topSplit[1], font),
            measure(botSplit[0], font), measure(botSplit[1], font)
          );
          if (maxW < bestMaxW) {
            bestMaxW = maxW;
            best = { lines: [...topSplit, ...botSplit], fontSize: size, isSmaller, needsManualFix: false, joinerOnLine: p.joinerOnLine, halfBreakAfterLine: 1 };
          }
        }
      }
      if (best) return best;
    }
  }

  // Nothing fits — fallback with first placement
  const fb = placements[0];
  return { lines: [fb.topChunk, fb.botChunk], fontSize: RECAP_SMALLER, isSmaller: true, needsManualFix: true, joinerOnLine: fb.joinerOnLine, halfBreakAfterLine: 0 };
}

/** Joiner placement: top and bottom text chunks with the joiner floated to one side */
interface JoinerPlacement {
  topChunk: string;
  botChunk: string;
  joinerOnLine: 1 | 2 | 'split' | undefined;
}

/**
 * Build joiner placement candidates — the joiner can be appended to the top chunk,
 * prepended to the bottom chunk, or split across both (for multi-word joiners).
 */
function getJoinerPlacements(top: string, joiner: string | null, bot: string): JoinerPlacement[] {
  if (!joiner) {
    return [{ topChunk: top, botChunk: bot, joinerOnLine: undefined }];
  }

  const placements: JoinerPlacement[] = [];
  const joinerWords = joiner.split(/\s+/);

  // Entire joiner appended to top
  placements.push({ topChunk: `${top} ${joiner}`, botChunk: bot, joinerOnLine: 1 });

  // Entire joiner prepended to bottom
  placements.push({ topChunk: top, botChunk: `${joiner} ${bot}`, joinerOnLine: 2 });

  // Split joiner across both (multi-word only)
  if (joinerWords.length > 1) {
    for (let i = 1; i < joinerWords.length; i++) {
      const j1 = joinerWords.slice(0, i).join(' ');
      const j2 = joinerWords.slice(i).join(' ');
      placements.push({ topChunk: `${top} ${j1}`, botChunk: `${j2} ${bot}`, joinerOnLine: 'split' });
    }
  }

  return placements;
}

// ─── Recap Answer Fitting ────────────────────────────────

const ANSWER_NORMAL = 37;
const ANSWER_SMALLER = 30;
const ANSWER_WEIGHT = 800;

function answerFont(size: number): string {
  return `${ANSWER_WEIGHT} ${size}px Inter, sans-serif`;
}

/**
 * Fit answer text + context line into a 208x120 rect.
 * Answer line at 37/30px weight 800, context at 21/17px weight 600.
 *
 * Each line is sized independently — a wide context line can shrink
 * without affecting the answer font size.
 */
export function fitRecapAnswerText(
  answer: string,
  contextText: string,
  width?: number,
  _hints?: DisplayHints
): TextFitResult {
  const available = (width ?? RECAP_RECT_WIDTH) - RECAP_RECT_PADDING;
  const ansUpper = answer.toUpperCase();
  const ctxUpper = contextText.toUpperCase();

  const af37 = answerFont(ANSWER_NORMAL);
  const af30 = answerFont(ANSWER_SMALLER);
  const cf21 = recapFont(RECAP_NORMAL);
  const cf17 = recapFont(RECAP_SMALLER);

  const ansFits37 = measure(ansUpper, af37) <= available;
  const ansFits30 = measure(ansUpper, af30) <= available;
  const ctxFits21 = measure(ctxUpper, cf21) <= available;
  const ctxFits17 = measure(ctxUpper, cf17) <= available;

  // Try combos from best to worst, sizing each line independently
  // 1. Answer 37 + Context 21 (both normal)
  if (ansFits37 && ctxFits21) {
    return { lines: [ansUpper, ctxUpper], fontSize: ANSWER_NORMAL, isSmaller: false, needsManualFix: false, contextFontSize: RECAP_NORMAL };
  }
  // 2. Answer 37 + Context 17 (answer normal, context shrunk)
  if (ansFits37 && ctxFits17) {
    return { lines: [ansUpper, ctxUpper], fontSize: ANSWER_NORMAL, isSmaller: false, needsManualFix: false, contextFontSize: RECAP_SMALLER };
  }
  // 3. Answer 30 + Context 21 (answer shrunk, context normal)
  if (ansFits30 && ctxFits21) {
    return { lines: [ansUpper, ctxUpper], fontSize: ANSWER_SMALLER, isSmaller: true, needsManualFix: false, contextFontSize: RECAP_NORMAL };
  }
  // 4. Answer 30 + Context 17 (both shrunk)
  if (ansFits30 && ctxFits17) {
    return { lines: [ansUpper, ctxUpper], fontSize: ANSWER_SMALLER, isSmaller: true, needsManualFix: false, contextFontSize: RECAP_SMALLER };
  }

  return { lines: [ansUpper, ctxUpper], fontSize: ANSWER_SMALLER, isSmaller: true, needsManualFix: true, contextFontSize: RECAP_SMALLER };
}

// ─── Recap Category Fitting ──────────────────────────────

/**
 * Fit category text into a teal rect.
 * 21/17px italic weight 600, 1-3 lines.
 *
 * Prefers normal font (21px) with more lines over smaller font (17px) with fewer:
 *   1-line 21px → 2-line 21px → 3-line 21px → 1-line 17px → 2-line 17px → 3-line 17px
 */
export function fitRecapCategoryText(
  category: string,
  width?: number,
  hints?: DisplayHints
): TextFitResult {
  const available = (width ?? RECAP_RECT_WIDTH) - RECAP_RECT_PADDING;
  const text = category.toUpperCase(); // categories displayed uppercase

  if (hints?.lineBreaks?.length) {
    const hintLines = applyDisplayHints(text, hints);
    const font21 = recapFont(RECAP_NORMAL);
    const fits21 = hintLines.every(l => measure(l, font21) <= available);
    if (fits21 && hintLines.length <= 3) {
      return { lines: hintLines, fontSize: RECAP_NORMAL, isSmaller: false, needsManualFix: false };
    }
    const font17 = recapFont(RECAP_SMALLER);
    const fits17 = hintLines.every(l => measure(l, font17) <= available);
    return {
      lines: hintLines,
      fontSize: RECAP_SMALLER,
      isSmaller: true,
      needsManualFix: !fits17,
    };
  }

  const font21 = recapFont(RECAP_NORMAL);

  // 1-line at 21px
  if (measure(text, font21) <= available) {
    return { lines: [text], fontSize: RECAP_NORMAL, isSmaller: false, needsManualFix: false };
  }

  // 2-line at 21px
  const split21_2 = bestSplit(text, font21, available);
  if (split21_2) {
    return { lines: split21_2, fontSize: RECAP_NORMAL, isSmaller: false, needsManualFix: false };
  }

  // 3-line at 21px
  const split21_3 = bestSplitMulti(text, font21, available, 3);
  if (split21_3) {
    return { lines: split21_3, fontSize: RECAP_NORMAL, isSmaller: false, needsManualFix: false };
  }

  // 1-line at 17px
  const font17 = recapFont(RECAP_SMALLER);
  if (measure(text, font17) <= available) {
    return { lines: [text], fontSize: RECAP_SMALLER, isSmaller: true, needsManualFix: false };
  }

  // 2-line at 17px
  const split17_2 = bestSplit(text, font17, available);
  if (split17_2) {
    return { lines: split17_2, fontSize: RECAP_SMALLER, isSmaller: true, needsManualFix: false };
  }

  // 3-line at 17px
  const split17_3 = bestSplitMulti(text, font17, available, 3);
  if (split17_3) {
    return { lines: split17_3, fontSize: RECAP_SMALLER, isSmaller: true, needsManualFix: false };
  }

  return { lines: [text], fontSize: RECAP_SMALLER, isSmaller: true, needsManualFix: true };
}

// ─── Solution Text Fitting ───────────────────────────────

const SOLUTION_NORMAL = 37;
const SOLUTION_SMALLER = 30;
const SOLUTION_WEIGHT = 800;

/**
 * Fit solution text into the solution banner area.
 * Single line only, never wraps. Matches recap answer font style (37/30px, weight 800).
 */
export function fitSolutionText(
  text: string,
  width: number
): TextFitResult {
  const upper = text.toUpperCase();
  const fontNormal = `${SOLUTION_WEIGHT} ${SOLUTION_NORMAL}px Inter, sans-serif`;
  if (measure(upper, fontNormal) <= width) {
    return { lines: [upper], fontSize: SOLUTION_NORMAL, isSmaller: false, needsManualFix: false };
  }
  const fontSmaller = `${SOLUTION_WEIGHT} ${SOLUTION_SMALLER}px Inter, sans-serif`;
  if (measure(upper, fontSmaller) <= width) {
    return { lines: [upper], fontSize: SOLUTION_SMALLER, isSmaller: true, needsManualFix: false };
  }
  return { lines: [upper], fontSize: SOLUTION_SMALLER, isSmaller: true, needsManualFix: true };
}

// ─── Split Helpers ───────────────────────────────────────

/**
 * Build an array of break opportunities from text.
 * Each opportunity is { pos: character index in `text` where the break occurs,
 *   line1: text for the first line, line2: text for the second line }.
 *
 * Break sources:
 *   - Word boundaries (spaces): split before the space, second line starts after it
 *   - Optional hyphens: split after the character at hyphen position, append '-' to line1
 *
 * `hyphenPositions` are character indices in the ORIGINAL (non-uppercased) text.
 * They correspond 1:1 with `text` since uppercasing doesn't change length.
 */
function gatherBreakOpportunities(
  text: string,
  hyphenPositions?: number[]
): Array<{ line1: string; line2: string }> {
  const opportunities: Array<{ line1: string; line2: string }> = [];

  // Word boundary breaks (at each space)
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ' ') {
      const line1 = text.slice(0, i);
      const line2 = text.slice(i + 1);
      if (line1 && line2) {
        opportunities.push({ line1, line2 });
      }
    }
  }

  // Optional hyphen breaks
  if (hyphenPositions) {
    for (const pos of hyphenPositions) {
      if (pos >= 0 && pos < text.length - 1) {
        const line1 = text.slice(0, pos + 1) + '-';
        const line2 = text.slice(pos + 1);
        if (line2) {
          opportunities.push({ line1, line2 });
        }
      }
    }
  }

  return opportunities;
}

/**
 * Find the best split into exactly 2 lines where both fit.
 * Considers word boundaries and optional hyphen positions.
 * Tries to balance line widths (most even split preferred).
 */
function bestSplit(
  text: string,
  font: string,
  maxWidth: number,
  hyphenPositions?: number[]
): string[] | null {
  const opportunities = gatherBreakOpportunities(text, hyphenPositions);
  if (opportunities.length === 0) return null;

  let bestResult: string[] | null = null;
  let bestDiff = Infinity;

  for (const { line1, line2 } of opportunities) {
    const w1 = measure(line1, font);
    const w2 = measure(line2, font);
    if (w1 <= maxWidth && w2 <= maxWidth) {
      const diff = Math.abs(w1 - w2);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestResult = [line1, line2];
      }
    }
  }

  return bestResult;
}

/**
 * Split text into up to maxLines lines at word boundaries.
 * Greedy: fill each line as much as possible.
 */
function bestSplitMulti(
  text: string,
  font: string,
  maxWidth: number,
  maxLines: number
): string[] | null {
  const words = text.split(/\s+/);
  if (words.length === 0) return [''];

  const lines: string[] = [];
  let current = words[0];

  for (let i = 1; i < words.length; i++) {
    const candidate = current + ' ' + words[i];
    if (measure(candidate, font) <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      if (lines.length >= maxLines) return null; // too many lines needed
      current = words[i];
    }
  }
  lines.push(current);

  if (lines.length > maxLines) return null;
  if (lines.some(l => measure(l, font) > maxWidth)) return null;

  return lines;
}

// ─── Clear Cache ─────────────────────────────────────────

/** Clear the measurement cache (e.g., after font loads) */
export function clearMeasureCache(): void {
  measureCache.clear();
}
